import { useState, useEffect } from 'react';
import { Bug, AlertCircle, AlertTriangle, AlertOctagon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

type Severity = 'low' | 'medium' | 'high' | 'critical';

interface BugReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  title: string;
  description: string;
  severity: Severity;
  pageUrl: string;
}

const severityOptions: { value: Severity; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'low', label: 'Low', icon: <AlertCircle className="h-4 w-4" />, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'medium', label: 'Medium', icon: <AlertTriangle className="h-4 w-4" />, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'high', label: 'High', icon: <AlertTriangle className="h-4 w-4" />, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'critical', label: 'Critical', icon: <AlertOctagon className="h-4 w-4" />, color: 'bg-red-100 text-red-700 border-red-200' },
];

export function BugReportDialog({ isOpen, onClose }: BugReportDialogProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    severity: 'medium',
    pageUrl: '',
  });

  // Update page URL when dialog opens
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      setFormData(prev => ({ ...prev, pageUrl: window.location.href }));
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase.from('bug_reports').insert({
        title: formData.title,
        description: formData.description,
        severity: formData.severity,
        page_url: formData.pageUrl,
        user_id: user?.id || null,
        status: 'open',
      });

      if (error) throw error;

      setSubmitStatus('success');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      severity: 'medium',
      pageUrl: '',
    });
    setSubmitStatus('idle');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border-none shadow-2xl p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-[#cb9ea3] to-[#bab3cd] p-6">
          <DialogHeader className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
                <Bug className="h-5 w-5 text-[#cb9ea3]" />
              </div>
              <DialogTitle className="text-2xl font-averia-bold text-white">
                Report a Bug
              </DialogTitle>
            </div>
            <DialogDescription className="text-white/90 text-base pl-[52px]">
              Help us improve TalkToPods by reporting any issues you encounter.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 pt-4">
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-averia-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600">Your bug report has been submitted successfully.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Title Input */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Bug Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  required
                  placeholder="e.g., Chat window not responding"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#cb9ea3] focus:ring-2 focus:ring-[#cb9ea3]/20 outline-none transition-all text-sm"
                />
              </div>

              {/* Severity Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Severity <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {severityOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, severity: option.value })}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all',
                        formData.severity === option.value
                          ? option.color
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      )}
                    >
                      {option.icon}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description Input */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  required
                  rows={4}
                  placeholder="Describe what happened, steps to reproduce, and what you expected..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#cb9ea3] focus:ring-2 focus:ring-[#cb9ea3]/20 outline-none transition-all resize-none text-sm"
                />
              </div>

              {/* Page URL (auto-filled) */}
              <div>
                <label htmlFor="pageUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  Page URL
                </label>
                <input
                  type="url"
                  id="pageUrl"
                  value={formData.pageUrl}
                  onChange={(e) => setFormData({ ...formData, pageUrl: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1.5">This helps us locate where the issue occurred</p>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                  Failed to submit bug report. Please try again.
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 rounded-full border-2 border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.title || !formData.description}
                  className="flex-1 rounded-full bg-[#cb9ea3] hover:bg-[#b88d92] text-white transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
